import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, message, Select, Progress, Row, Col, Checkbox, Radio } from 'antd';
import {
    UserOutlined, PhoneOutlined, MailOutlined, LockOutlined,
    CheckCircleOutlined, ArrowLeftOutlined, ArrowRightOutlined,
    TeamOutlined, FlagOutlined, GlobalOutlined, HeartOutlined,
    ClockCircleOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import { ambassadorService, cityService } from '../../common/services/api';
import './AmbassadorRegister.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AmbassadorRegisterPage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [hasCommunity, setHasCommunity] = useState(false);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const loadCities = async () => {
            try {
                const response = await cityService.getActiveCities();
                if (response.success) {
                    setCities(response.data || []);
                }
            } catch (error) {
                console.error('Erreur chargement villes:', error);
            }
        };
        loadCities();
    }, []);

    const totalSteps = 5;
    const progressPercent = ((currentStep + 1) / totalSteps) * 100;

    const stepFields = {
        0: ['firstName', 'lastName', 'phone', 'email', 'city'],
        1: ['profileType', 'hasCommunity'],
        2: ['motivation', 'promotionMethods'],
        3: ['availability', 'acceptsRepresentation', 'acceptsTerms', 'password', 'confirmPassword'],
        4: [] // Recap step - no validation needed
    };

    const getProfileTypeLabel = (value) => {
        const labels = {
            'user_client': 'Utilisateur / Client',
            'professional': 'Professionnel du secteur',
            'driver_operator': 'Chauffeur / Operateur terrain',
            'influencer': 'Influenceur / Createur de contenu',
            'entrepreneur_leader': 'Entrepreneur / Leader communautaire',
            'other': 'Autre'
        };
        return labels[value] || value;
    };

    const getPromotionMethodLabel = (value) => {
        const labels = {
            'word_of_mouth': 'Bouche-a-oreille',
            'social_media': 'Reseaux sociaux',
            'field_actions': 'Terrain / actions locales',
            'professional_recommendations': 'Recommandations professionnelles',
            'other': 'Autre'
        };
        return labels[value] || value;
    };

    const getAvailabilityLabel = (value) => {
        const labels = {
            'occasional': 'Occasionnel',
            'regular': 'Regulier',
            'very_active': 'Tres actif'
        };
        return labels[value] || value;
    };

    const getCommunitySizeLabel = (value) => {
        const labels = {
            'moins_100': 'Moins de 100 personnes',
            '100_500': '100 - 500 personnes',
            '500_1000': '500 - 1000 personnes',
            '1000_5000': '1000 - 5000 personnes',
            'plus_5000': 'Plus de 5000 personnes'
        };
        return labels[value] || value;
    };

    const validateStep = async () => {
        try {
            await form.validateFields(stepFields[currentStep]);
            return true;
        } catch (error) {
            return false;
        }
    };

    const nextStep = async () => {
        const isValid = await validateStep();
        if (isValid && currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = {
                firstName: values.firstName?.toUpperCase(),
                lastName: values.lastName?.toUpperCase(),
                phone: values.phone,
                email: values.email,
                password: values.password,
                city: values.city,
                country: 'Côte d\'Ivoire',
                profileType: values.profileType || [],
                profileTypeOther: values.profileTypeOther,
                hasCommunity: values.hasCommunity === 'yes',
                communitySize: values.communitySize,
                socialNetworks: values.socialNetworks,
                socialLinks: values.socialLinks,
                motivation: values.motivation,
                promotionMethods: values.promotionMethods || [],
                promotionMethodOther: values.promotionMethodOther,
                availability: values.availability,
                acceptsTerms: values.acceptsTerms,
                acceptsRepresentation: values.acceptsRepresentation
            };

            const response = await ambassadorService.submitApplication(formData);
            if (response.success) {
                setSubmitted(true);
            } else {
                message.error(response.message || 'Erreur lors de la soumission');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de la soumission');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="register-page">
                <div className="register-success">
                    <div className="success-content">
                        <div className="success-icon-wrapper">
                            <CheckCircleOutlined />
                        </div>
                        <Title level={2}>Candidature envoyee !</Title>
                        <Paragraph>
                            Merci pour votre candidature au programme Brand Ambassador.
                            Notre equipe va examiner votre dossier et vous contacter sous 48h.
                        </Paragraph>
                        <Paragraph type="secondary">
                            Vous pouvez vous connecter a votre espace pour suivre l'etat de votre candidature.
                        </Paragraph>
                        <div className="success-actions">
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => navigate('/ambassadeurs/connexion')}
                            >
                                Se connecter
                            </Button>
                            <Button
                                size="large"
                                onClick={() => navigate('/ambassadeurs')}
                            >
                                Retour a l'accueil
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="register-page">
            {/* Left Branding Panel */}
            <div className="register-branding">
                <Link to="/ambassadeurs" className="back-link">
                    <ArrowLeftOutlined /> Retour
                </Link>

                <div className="branding-content">
                    <div className="branding-badge">
                        <FlagOutlined /> Brand Ambassador
                    </div>
                    <Title level={2}>Rejoignez le programme</Title>
                    <Paragraph>
                        Devenez Brand Ambassador et partagez votre passion pour TIGOON Connect avec votre communaute.
                    </Paragraph>

                    <div className="benefits-list">
                        <div className="benefit-item">
                            <TeamOutlined />
                            <span>Creez et gerez vos affilies</span>
                        </div>
                        <div className="benefit-item">
                            <GlobalOutlined />
                            <span>Developpez votre reseau</span>
                        </div>
                        <div className="benefit-item">
                            <HeartOutlined />
                            <span>Partenariat privilegie avec TIGOON</span>
                        </div>
                    </div>
                </div>

                <div className="branding-footer">
                    <Text type="secondary">
                        Deja ambassadeur ? <Link to="/ambassadeurs/connexion">Se connecter</Link>
                    </Text>
                </div>
            </div>

            {/* Right Form Panel */}
            <div className="register-form-panel">
                <div className="form-container">
                    <div className="form-header">
                        <Title level={3}>Formulaire d'inscription</Title>
                        <div className="step-indicator">
                            Etape {currentStep + 1} sur {totalSteps}
                        </div>
                    </div>

                    <Progress
                        percent={progressPercent}
                        showInfo={false}
                        strokeColor="#D4A853"
                        className="progress-bar"
                    />

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        requiredMark={false}
                    >
                        {/* Step 1: Personal Info */}
                        <div className={`form-step ${currentStep === 0 ? 'active' : ''}`}>
                            <div className="step-title">
                                <UserOutlined /> Informations personnelles
                            </div>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="firstName"
                                        label="Prenom"
                                        rules={[{ required: true, message: 'Requis' }]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="Prenom"
                                            size="large"
                                            style={{ textTransform: 'uppercase' }}
                                            onChange={(e) => form.setFieldValue('firstName', e.target.value.toUpperCase())}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="lastName"
                                        label="Nom"
                                        rules={[{ required: true, message: 'Requis' }]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="Nom"
                                            size="large"
                                            style={{ textTransform: 'uppercase' }}
                                            onChange={(e) => form.setFieldValue('lastName', e.target.value.toUpperCase())}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item
                                name="phone"
                                label="Telephone / WhatsApp"
                                rules={[
                                    { required: true, message: 'Requis' },
                                    { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres' }
                                ]}
                            >
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="Ex: 0700000000"
                                    size="large"
                                    maxLength={10}
                                    onChange={(e) => {
                                        const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                                        form.setFieldValue('phone', cleanValue);
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: 'Email requis' },
                                    { type: 'email', message: 'Email invalide' }
                                ]}
                                extra="Une adresse valide est necessaire pour vous contacter"
                            >
                                <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="city"
                                label="Ville"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Select
                                    placeholder="Selectionnez une ville"
                                    size="large"
                                    showSearch
                                    optionFilterProp="children"
                                    suffixIcon={<EnvironmentOutlined />}
                                >
                                    {cities.map(city => (
                                        <Option key={city._id} value={city._id}>{city.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        {/* Step 2: Profile & Community */}
                        <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                            <div className="step-title">
                                <TeamOutlined /> Profil de l'Ambassadeur
                            </div>
                            <Form.Item
                                name="profileType"
                                label="Quel est votre profil principal ?"
                                rules={[{ required: true, message: 'Selectionnez au moins un profil' }]}
                            >
                                <Checkbox.Group style={{ width: '100%' }}>
                                    <Row>
                                        <Col span={12}>
                                            <Checkbox value="user_client">Utilisateur / Client</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="professional">Professionnel du secteur</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="driver_operator">Chauffeur / Operateur terrain</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="influencer">Influenceur / Createur de contenu</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="entrepreneur_leader">Entrepreneur / Leader communautaire</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="other">Autre</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) => prev.profileType !== curr.profileType}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('profileType')?.includes('other') && (
                                        <Form.Item name="profileTypeOther" label="Precisez">
                                            <Input placeholder="Precisez votre profil" size="large" />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>

                            <Form.Item
                                name="hasCommunity"
                                label="Avez-vous deja une communaute ?"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Radio.Group onChange={(e) => setHasCommunity(e.target.value === 'yes')}>
                                    <Radio value="yes">Oui</Radio>
                                    <Radio value="no">Non</Radio>
                                </Radio.Group>
                            </Form.Item>
                            {hasCommunity && (
                                <Form.Item
                                    name="communitySize"
                                    label="Taille approximative de votre communaute"
                                >
                                    <Select placeholder="Selectionnez" size="large">
                                        <Option value="moins_100">Moins de 100 personnes</Option>
                                        <Option value="100_500">100 - 500 personnes</Option>
                                        <Option value="500_1000">500 - 1000 personnes</Option>
                                        <Option value="1000_5000">1000 - 5000 personnes</Option>
                                        <Option value="plus_5000">Plus de 5000 personnes</Option>
                                    </Select>
                                </Form.Item>
                            )}

                            <div className="step-title" style={{ marginTop: 24 }}>
                                <GlobalOutlined /> Presence digitale
                            </div>
                            <Form.Item
                                name="socialNetworks"
                                label="Reseaux sociaux / plateformes utilisees"
                            >
                                <Input placeholder="Ex: Facebook, WhatsApp, TikTok, Instagram, YouTube" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="socialLinks"
                                label="Lien(s) vers vos pages ou groupes"
                            >
                                <TextArea
                                    placeholder="Collez les liens vers vos pages, groupes ou profils"
                                    rows={2}
                                />
                            </Form.Item>
                        </div>

                        {/* Step 3: Motivation & Engagement */}
                        <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                            <div className="step-title">
                                <HeartOutlined /> Motivation & engagement
                            </div>
                            <Form.Item
                                name="motivation"
                                label="Pourquoi souhaitez-vous devenir Ambassadeur ?"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <TextArea
                                    placeholder="Expliquez vos motivations..."
                                    rows={4}
                                />
                            </Form.Item>
                            <Form.Item
                                name="promotionMethods"
                                label="Comment comptez-vous promouvoir notre marque ?"
                                rules={[{ required: true, message: 'Selectionnez au moins une methode' }]}
                            >
                                <Checkbox.Group style={{ width: '100%' }}>
                                    <Row>
                                        <Col span={12}>
                                            <Checkbox value="word_of_mouth">Bouche-a-oreille</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="social_media">Reseaux sociaux</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="field_actions">Terrain / actions locales</Checkbox>
                                        </Col>
                                        <Col span={12}>
                                            <Checkbox value="professional_recommendations">Recommandations professionnelles</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="other">Autre</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) => prev.promotionMethods !== curr.promotionMethods}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('promotionMethods')?.includes('other') && (
                                        <Form.Item name="promotionMethodOther" label="Precisez">
                                            <Input placeholder="Precisez votre methode" size="large" />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>
                        </div>

                        {/* Step 4: Availability & Terms */}
                        <div className={`form-step ${currentStep === 3 ? 'active' : ''}`}>
                            <div className="step-title">
                                <ClockCircleOutlined /> Disponibilite
                            </div>
                            <Form.Item
                                name="availability"
                                label="Combien de temps pouvez-vous consacrer au programme ?"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Radio.Group>
                                    <Radio.Button value="occasional">Occasionnel</Radio.Button>
                                    <Radio.Button value="regular">Regulier</Radio.Button>
                                    <Radio.Button value="very_active">Tres actif</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            <div className="step-title" style={{ marginTop: 24 }}>
                                <CheckCircleOutlined /> Engagement & conditions
                            </div>
                            <Form.Item
                                name="acceptsRepresentation"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject('Vous devez accepter')
                                    }
                                ]}
                            >
                                <Checkbox>
                                    J'accepte de representer la marque de maniere responsable et conforme a ses valeurs
                                </Checkbox>
                            </Form.Item>
                            <Form.Item
                                name="acceptsTerms"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject('Vous devez accepter les conditions')
                                    }
                                ]}
                            >
                                <Checkbox>
                                    J'accepte les conditions du Programme Ambassadeur
                                </Checkbox>
                            </Form.Item>

                            <div className="step-title" style={{ marginTop: 24 }}>
                                <LockOutlined /> Securite
                            </div>
                            <Form.Item
                                name="password"
                                label="Mot de passe"
                                rules={[
                                    { required: true, message: 'Requis' },
                                    { min: 6, message: 'Minimum 6 caracteres' }
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Mot de passe" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="confirmPassword"
                                label="Confirmer le mot de passe"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Requis' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Confirmer" size="large" />
                            </Form.Item>
                        </div>

                        {/* Step 5: Recap */}
                        <div className={`form-step ${currentStep === 4 ? 'active' : ''}`}>
                            <div className="step-title">
                                <CheckCircleOutlined /> Recapitulatif de votre candidature
                            </div>
                            <div className="recap-section">
                                <div className="recap-group">
                                    <h4><UserOutlined /> Informations personnelles</h4>
                                    <div className="recap-item">
                                        <span className="recap-label">Prenom:</span>
                                        <span className="recap-value">{form.getFieldValue('firstName')}</span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Nom:</span>
                                        <span className="recap-value">{form.getFieldValue('lastName')}</span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Telephone:</span>
                                        <span className="recap-value">{form.getFieldValue('phone')}</span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Email:</span>
                                        <span className="recap-value">{form.getFieldValue('email')}</span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Ville:</span>
                                        <span className="recap-value">{form.getFieldValue('city')}</span>
                                    </div>
                                </div>

                                <div className="recap-group">
                                    <h4><TeamOutlined /> Profil de l'Ambassadeur</h4>
                                    <div className="recap-item">
                                        <span className="recap-label">Profil:</span>
                                        <span className="recap-value">
                                            {form.getFieldValue('profileType')?.map(p => getProfileTypeLabel(p)).join(', ')}
                                            {form.getFieldValue('profileTypeOther') && ` (${form.getFieldValue('profileTypeOther')})`}
                                        </span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Communaute:</span>
                                        <span className="recap-value">
                                            {form.getFieldValue('hasCommunity') === 'yes' ? 'Oui' : 'Non'}
                                            {form.getFieldValue('hasCommunity') === 'yes' && form.getFieldValue('communitySize') &&
                                                ` - ${getCommunitySizeLabel(form.getFieldValue('communitySize'))}`}
                                        </span>
                                    </div>
                                    {form.getFieldValue('socialNetworks') && (
                                        <div className="recap-item">
                                            <span className="recap-label">Reseaux sociaux:</span>
                                            <span className="recap-value">{form.getFieldValue('socialNetworks')}</span>
                                        </div>
                                    )}
                                    {form.getFieldValue('socialLinks') && (
                                        <div className="recap-item">
                                            <span className="recap-label">Liens:</span>
                                            <span className="recap-value">{form.getFieldValue('socialLinks')}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="recap-group">
                                    <h4><HeartOutlined /> Motivation & engagement</h4>
                                    <div className="recap-item">
                                        <span className="recap-label">Motivation:</span>
                                        <span className="recap-value">{form.getFieldValue('motivation')}</span>
                                    </div>
                                    <div className="recap-item">
                                        <span className="recap-label">Methodes de promotion:</span>
                                        <span className="recap-value">
                                            {form.getFieldValue('promotionMethods')?.map(m => getPromotionMethodLabel(m)).join(', ')}
                                            {form.getFieldValue('promotionMethodOther') && ` (${form.getFieldValue('promotionMethodOther')})`}
                                        </span>
                                    </div>
                                </div>

                                <div className="recap-group">
                                    <h4><ClockCircleOutlined /> Disponibilite</h4>
                                    <div className="recap-item">
                                        <span className="recap-label">Disponibilite:</span>
                                        <span className="recap-value">{getAvailabilityLabel(form.getFieldValue('availability'))}</span>
                                    </div>
                                </div>

                                <div className="recap-notice">
                                    <CheckCircleOutlined /> Vous avez accepte de representer la marque de maniere responsable
                                    <br />
                                    <CheckCircleOutlined /> Vous avez accepte les conditions du Programme Ambassadeur
                                </div>
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="form-navigation">
                            {currentStep > 0 && (
                                <Button size="large" onClick={prevStep} icon={<ArrowLeftOutlined />}>
                                    Precedent
                                </Button>
                            )}
                            {currentStep < totalSteps - 1 ? (
                                <Button type="primary" size="large" onClick={nextStep}>
                                    Suivant <ArrowRightOutlined />
                                </Button>
                            ) : (
                                <Button type="primary" size="large" htmlType="submit" loading={loading}>
                                    Soumettre ma candidature
                                </Button>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default AmbassadorRegisterPage;
